import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { LogOut, UserCog } from "lucide-react";

import { MENU_ITEMS } from "components/Sidebar/config/menuConfig";
import { useUserRole } from "components/Sidebar/hooks/useUserRole";
import { formatTooltipTitle } from "components/Sidebar/utils/formatTooltipTitle";
import { LogoutButton } from "features/auth/components/LogoutButton";
import { useOverdueLabTestsCount } from "features/labOrders/hooks/useOverdueLabTestsCount";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { useUnlinkedMncmsCount } from "features/pregnancy/hooks/useUnlinkedMncmsCount";
import { useUnassignedResultsCount } from "features/results/hooks/useUnassignedResultsCount";
import { pathGenerators } from "utils/routes";
import { isTenantTypeAllowed } from "utils/tenantAccess";

const ICON_SIZE = 22;

const HEALTHLINK_HIDDEN_IDS = ["results", "cdm"];

// The one menu item that carries a count, and it carries three numbers added together:
// a result with no patient, a maternity message with no patient and a lab test the
// laboratory is late answering are the same problem — nothing surfaces them anywhere
// else — and all three are worked from the results control centre, so one total is what
// the practice needs to see.
const RESULTS_ID = "results";

export const SidebarMenuLinks = ({
  styles,
  isActive,
  onMenuClick,
  userrole,
  isAdmin,
  hasAccess: hasAccessFn,
}) => {
  const { data: practiceSettings } = usePracticeSettings();
  const { tenantType } = useUserRole();

  const hasMenuAccess = useCallback(
    (item) => {
      if (item.adminOnly) {
        if (isAdmin) return true;
        if (hasAccessFn) return hasAccessFn(item.roles);
        return false;
      }
      // Empty roles array = visible to everyone
      if (item.roles.length === 0) return true;
      if (!userrole) return false;
      if (hasAccessFn) return hasAccessFn(item.roles);
      return item.roles.some(
        (role) => userrole.toUpperCase() === role.toUpperCase()
      );
    },
    [isAdmin, hasAccessFn, userrole]
  );

  const visibleItems = useMemo(() => {
    let items = MENU_ITEMS.filter(hasMenuAccess);
    if (practiceSettings?.healthLink === false) {
      items = items.filter((item) => !HEALTHLINK_HIDDEN_IDS.includes(item.id));
    }
    items = items.filter((item) =>
      isTenantTypeAllowed(item.tenantTypes, tenantType)
    );
    return items;
  }, [practiceSettings, hasMenuAccess, tenantType]);

  // Not fetched for a practice that can't see the item at all.
  const showsResults = visibleItems.some((item) => item.id === RESULTS_ID);
  const { data: unlinkedMaternityCount } = useUnlinkedMncmsCount({
    enabled: showsResults,
  });
  const { data: unassignedResultsCount } = useUnassignedResultsCount({
    enabled: showsResults,
  });
  // Overdue, not outstanding (KIN-2087): a practice always has tests in flight, so a
  // count of everything outstanding would never reach zero and would stop being read.
  const { data: overdueLabTestsCount } = useOverdueLabTestsCount({
    enabled: showsResults,
  });

  const badgeCounts = {
    [RESULTS_ID]:
      (unassignedResultsCount ?? 0) +
      (unlinkedMaternityCount ?? 0) +
      (overdueLabTestsCount ?? 0),
  };

  return (
    <>
      {visibleItems.map((item) => {
        const Icon = item.Icon;
        const count = badgeCounts[item.id];
        const badge = count > 0 ? count : null;

        return (
          <Link
            key={item.id}
            to={item.path}
            className={`${styles.menuItem} ${
              isActive(item.path) ? styles.active : ""
            }`}
            /*
             * The name is the label and nothing else, with or without a count. The badge is
             * visible text inside the link, so without this the link was named "3Results" — to a
             * screen reader, and to anything addressing it by name.
             *
             * The count deliberately does NOT go in the name. Folding it in ("Results, 3 needing
             * attention") only moves the problem: the name still changes with the data, so
             * anything holding a reference by name still loses it the moment something needs
             * attention. It belongs in the description, which is what aria-describedby is for.
             */
            aria-label={item.label}
            aria-describedby={badge ? `${item.id}-badge-count` : undefined}
            onClick={onMenuClick}
          >
            <Icon
              className={styles.sidebarIcon}
              size={ICON_SIZE}
              strokeWidth={2}
              aria-hidden
            />
            {/* The digit is hidden from assistive technology; the sentence beside it is what is
                announced, and it is the description rather than the name. */}
            {!!badge && (
              <>
                <span className={styles.badge} aria-hidden>
                  {badge}
                </span>
                <span id={`${item.id}-badge-count`} className={styles.srOnly}>
                  {badge} needing attention
                </span>
              </>
            )}
            <span className={styles.tooltip}>
              {formatTooltipTitle(item.label)}
            </span>
          </Link>
        );
      })}
      <Link
        to={pathGenerators.settings.profile()}
        className={`${styles.menuItem} ${
          isActive(pathGenerators.settings.profile()) ? styles.active : ""
        }`}
        onClick={onMenuClick}
      >
        <UserCog
          className={styles.sidebarIcon}
          size={ICON_SIZE}
          strokeWidth={2}
          aria-hidden
        />
        <span className={styles.tooltip}>Profile</span>
      </Link>
      <LogoutButton
        className={styles.menuItem}
        onTriggerClick={onMenuClick}
      >
        <LogOut
          className={styles.sidebarIcon}
          size={ICON_SIZE}
          strokeWidth={2}
          aria-hidden
        />
        <span className={styles.tooltip}>Logout</span>
      </LogoutButton>
    </>
  );
};
